import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
// import { Grid, Menu, MenuItem, Popper, Box } from '@mui/material';
import { LinearProgress,Grid , Menu, MenuItem, Popper, Box,  Dialog, DialogActions, DialogTitle ,DialogContent, FormControlLabel,  SpeedDial, SpeedDialAction,Button } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { WheelPeekTrigger } from './WheelPeekTrigger';
import { exportToPDF } from './ExportPDF.js';
import { formatAlarmDuration } from './alarmDuration';
import { buildPreAlarmContextPresentation } from './preAlarmContext';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ScappSwitch from '../../utils/ScappSwitch';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import {
  alarmReactionPlugin,
  generateTasaLegendLabels,
  sampleCurrentDirections,
  currentDirectionPlugin,
  hoverColumnPlugin,
  yellow,
  currentColor,
  CURRENT_ARROW_BAND,
  CURRENT_ARROW_LEN,
} from './TasaChartPlugins';
import { FEEDING_RATES, rateDays, resolveRateKey } from '../../../utils/feedingRates';
import { parseScappLocalDate as parseDateLocal } from '../../../utils/parseScappDate';

// Re-exporta los plugins para mantener compatibilidad con imports existentes (p. ej. tests).
export { alarmReactionPlugin, generateTasaLegendLabels, sampleCurrentDirections, currentDirectionPlugin };

Chart.register(zoomPlugin);

// Colores
const orangeLine = 'rgba(232, 78, 14, 0.55)';
const orangeBG   = 'rgba(232, 78, 14, 0.08)';
const greenLine  = 'rgba(85, 187, 69, 0.55)';
const redLine    = 'rgba(171, 2,  2,   0.9)';
const redText    = 'rgba(172, 20, 20, 0.9)';
const yellowText = 'rgba(255, 206, 86, 0.85)';
const unattendedAlarmColor = 'rgba(233, 30, 99, 0.9)';
const cyanLine   = 'rgba(53, 177, 230, 0.9)';
const tempLine   = 'rgba(233, 30, 99, 0.8)';
const tempText   = 'rgba(233, 30, 99, 0.9)';
const noteColor  = 'rgba(142, 109, 232, 0.95)';

const lineGraphConfig = { fill: false, stepped: false, pointRadius: 1, pointHoverRadius: 6 };
const areaGraphConfig = { fill: 'start', stepped: true, pointRadius: 0, pointHoverRadius: 6 };

export const buildTemperatureScale = (fontSize, display) => ({
  type: 'linear',
  position: 'left',
  display,
  suggestedMin: 5,
  suggestedMax: 15,
  title: { display: false },
  ticks: { font: { size: fontSize }, color: tempText, precision: 2, callback: (value) => Number(value).toFixed(2) },
  grid: { drawOnChartArea: false },
});

export const nextOxygenMode = (mode, hasSo2, hasO2) => {
  if (hasSo2 && hasO2) return mode === 'so2' ? 'o2' : mode === 'o2' ? 'hidden' : 'so2';
  return mode === 'hidden' ? (hasSo2 ? 'so2' : 'o2') : 'hidden';
};

export const pointMetric = (point, key) => {
  const source = point?.synthetic && point?.tooltipSource ? point.tooltipSource : point;
  return source?.snapshot?.[key] ?? source?.[key];
};

export const tooltipMetricLabel = (label, metricKey, usesExactSnapshot, instantaneous) => (
  metricKey === 'activity' && usesExactSnapshot ? `${label} (${instantaneous})` : label
);

export { buildPreAlarmContextPresentation } from './preAlarmContext';

const formatContextSpan = (seconds) => (
  seconds >= 60 && seconds % 60 === 0 ? `${seconds / 60} min` : `${formatMetric(seconds)} s`
);

export const firstOxygenSource = (sources = []) => sources.find((source) =>
  Array.isArray(source?.value)
    && source.value.some((point) => isPresent(pointMetric(point, 'so2')) || isPresent(pointMetric(point, 'o2')))
);

// Temperatura del sensor de agua (llega inline en graph_activity, igual que oxígeno).
export const firstTempSource = (sources = []) => sources.find((source) =>
  Array.isArray(source?.value)
    && source.value.some((point) => isPresent(pointMetric(point, 'temp')))
);

const notificationPath = 'M12 22c1.1 0 1.99-.9 1.99-2h-4c0 1.1.9 2 2.01 2zM18 16v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1z';
const notificationImportantPath = 'M12 23c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm9-4v-1l-2-2v-5c0-3.08-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S11.5 3.17 11.5 4v.68C8.63 5.36 7 7.92 7 11v5l-2 2v1h16zm-8-3h-2v-2h2v2zm0-4h-2V8h2v4z';
const commentPath = 'M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM18 14H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z';

const createMaterialPointStyle = (path, color) => {
  if (typeof document === 'undefined' || typeof Path2D === 'undefined') return 'rectRot';
  const canvas = document.createElement('canvas');
  canvas.width = 18;
  canvas.height = 18;
  const context = canvas.getContext('2d');
  if (!context) return 'rectRot';
  context.scale(0.75, 0.75);
  context.fillStyle = color;
  context.fill(new Path2D(path));
  return canvas;
};

export const getAlarmPresentation = (alarm, showAlarmReaction) => {
  const unattended = Boolean(showAlarmReaction && alarm?.atendido === false);
  return {
    color: unattended ? unattendedAlarmColor : yellowText,
    markerColor: unattended ? unattendedAlarmColor : yellow,
    icon: unattended ? 'important' : 'notification',
  };
};

export const formatAlarmReaction = (alarm, translate) => {
  if (alarm?.atendido === false) return translate('analytics_alarm_unattended', 'No atendida');
  const hasLatency = alarm?.latency_sec !== null && alarm?.latency_sec !== undefined && alarm?.latency_sec !== '';
  const latency = hasLatency ? Number(alarm.latency_sec) : NaN;
  if (alarm?.atendido === true && Number.isFinite(latency)) return `${latency} [s]`;
  if (alarm?.atendido === true) return translate('analytics_alarm_attended', 'Atendida');
  return translate('analytics_alarm_reaction_unavailable', 'No evaluable');
};

export const getAlarmReactionEnd = (alarm, showAlarmReaction) => {
  if (!showAlarmReaction || alarm?.atendido !== true || typeof alarm?.start !== 'string') return null;
  const hasLatency = alarm.latency_sec !== null && alarm.latency_sec !== undefined && alarm.latency_sec !== '';
  const latency = hasLatency ? Number(alarm.latency_sec) : NaN;
  const start = parseDateLocal(alarm.start).getTime();
  return Number.isFinite(start) && Number.isFinite(latency) && latency >= 0
    ? start + latency * 1000
    : null;
};

export const buildNotePoints = (notes) => (Array.isArray(notes) ? notes : [])
  .map((note) => ({
    x: new Date(String(note.timestamp || '').replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '')).getTime(),
    y: 25,
    noteData: note,
  }))
  .filter((point) => Number.isFinite(point.x));

export const normalizeNoteDayKey = (timestamp) => {
  const rawValue = String(timestamp || '').trim();
  const dayFirst = rawValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dayFirst) {
    return [
      String(Number(dayFirst[1])).padStart(2, '0'),
      String(Number(dayFirst[2])).padStart(2, '0'),
      dayFirst[3],
    ].join('/');
  }

  const parsed = new Date(rawValue.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, ''));
  if (Number.isNaN(parsed.getTime())) return '';
  return [
    String(parsed.getDate()).padStart(2, '0'),
    String(parsed.getMonth() + 1).padStart(2, '0'),
    parsed.getFullYear(),
  ].join('/');
};

const buildNoteJaulaKeysByCameraId = (analyticsData, compact) => Object.entries(
  analyticsData || {},
).reduce((index, [cameraId, jaula]) => {
  const visibleKey = compact
    ? String(jaula?.name || '').replace(/[^0-9]/g, '')
    : jaula?.name;
  const cameraIds = [cameraId, ...(jaula?.note_camera_ids || [])];
  cameraIds.forEach((sourceCameraId) => {
    index[sourceCameraId] = Array.from(new Set([sourceCameraId, visibleKey].filter(Boolean)));
  });
  return index;
}, {});

export const buildNotesByJaulaDay = (notes, analyticsData, compact) => {
  const jaulaKeysByCameraId = buildNoteJaulaKeysByCameraId(analyticsData, compact);
  return (notes || []).reduce((index, note) => {
    const day = normalizeNoteDayKey(note.timestamp);
    if (!day) return index;
    const jaulaKeys = new Set((note.cam_ids || []).flatMap(
      (cameraId) => jaulaKeysByCameraId[cameraId] || [cameraId],
    ));
    jaulaKeys.forEach((jaulaKey) => {
      index[jaulaKey] ??= {};
      index[jaulaKey][day] ??= [];
      index[jaulaKey][day].push(note);
    });
    return index;
  }, {});
};


const TasaChart = ({
  chartData,
  jaulaSeleccionada,
  selectedDay,
  selectedRateKey = 'auto',
  cardStyles,
  compact,
  translate,
  showAlarmReaction = false,
  notesByJaulaDay = {},
}) => {
  const t = (key, fallback) => {
    const text = typeof translate === 'function' ? translate(key) : key;
    return text && text !== key ? text : fallback;
  };
  const lineChartRef = useRef(null);
  const lineChartInstanceRef = useRef(null);
  const expandedCanvasRef = useRef(null);
  const modalChartRef = useRef(null);
  const notificationPointStyle = useMemo(() => createMaterialPointStyle(notificationPath, yellow), []);
  const notificationImportantPointStyle = useMemo(
    () => createMaterialPointStyle(notificationImportantPath, unattendedAlarmColor),
    [],
  );
  const notePointStyle = useMemo(() => createMaterialPointStyle(commentPath, noteColor), []);

  const [progressPDF, setProgressPDF] = useState(0);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [oxygenMode, setOxygenMode] = useState('so2');
  // Estado modal expandido
  const [expanded, setExpanded] = useState(false);
  // lÃ­mites originales de X para zoom/reset
  const originalXRef = useRef({ min: null, max: null });

  // MenÃºs
  const [anchorJaula, setAnchorJaula] = useState(null);
  const [anchorDay, setAnchorDay] = useState(null);

    // Modal
  const [anchorJaulaModal, setAnchorJaulaModal] = useState(null);
  const [anchorDayModal, setAnchorDayModal] = useState(null);

  const [defaultDay, setDefaultDay] = useState(null);

  // Estado local espejo de props
  const [localJaula, setLocalJaula] = useState(jaulaSeleccionada);
  const [localDay, setLocalDay] = useState(selectedDay);
    // ======= Handlers de apertura/cierre (apagan/encienden zoom wheel) =======
  const setWheelZoomEnabled = useCallback((enabled) => {
    const charts = [lineChartInstanceRef.current, modalChartRef.current].filter(Boolean);
    charts.forEach((c) => {
      if (!c) return;
      const z = c.options?.plugins?.zoom;
      if (z?.zoom?.wheel) {
        z.zoom.wheel.enabled = enabled;
        c.update('none');
      }
    });
  }, []);

  // const handleOpenJaula = (e) => setAnchorJaula(e.currentTarget);
  // const handleCloseJaula = () => setAnchorJaula(null);
  // const handleOpenDay = (e) => setAnchorDay(e.currentTarget);
  // const handleCloseDay = () => setAnchorDay(null);

  // Compacto
  const handleOpenJaula = (e) => { setAnchorJaula(e.currentTarget); setWheelZoomEnabled(false); };
  const handleCloseJaula = () => { setAnchorJaula(null); setWheelZoomEnabled(true); };
  const handleOpenDay = (e) => { setAnchorDay(e.currentTarget); setWheelZoomEnabled(false); };
  const handleCloseDay = () => { setAnchorDay(null); setWheelZoomEnabled(true); };

  // Modal
  const handleOpenJaulaModal = (e) => { setAnchorJaulaModal(e.currentTarget); setWheelZoomEnabled(false); };
  const handleCloseJaulaModal = () => { setAnchorJaulaModal(null); setWheelZoomEnabled(true); };
  const handleOpenDayModal = (e) => { setAnchorDayModal(e.currentTarget); setWheelZoomEnabled(false); };
  const handleCloseDayModal = () => { setAnchorDayModal(null); setWheelZoomEnabled(true); };


  const borderWidth = 0.2;
  const colorLine = 'rgba(126, 121, 121, 0.38)';
  const fontSizeEjes = window.innerWidth * 0.0065;

  const [activeJaulaIndex, setActiveJaulaIndex] = useState(0);
  const [activeDayIndex, setActiveDayIndex] = useState(0);

  const displayedJaula = localJaula || (chartData && chartData.length > 0 ? chartData[0].name : '');

  // Jaulas ordenadas (por nombre)
  const orderedJaulas = useMemo(() => {
    return (Array.isArray(chartData) ? chartData.map((j, srcIdx) => ({ name: j?.name ?? '', label: j?.label, srcIdx })) : [])
      .filter(x => typeof x.name === 'string')
      .sort((a, b) => a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase(), 'es', { sensitivity: 'base' }));
  }, [chartData]);

  // Jaula activa real
  const activeJaulaObj = useMemo(() => {
    const src = orderedJaulas[activeJaulaIndex]?.srcIdx ?? 0;
    return chartData?.[src] ?? chartData?.[0];
  }, [orderedJaulas, activeJaulaIndex, chartData]);

  // DÃ­as ordenados crecientemente
  const orderedDays = useMemo(() => {
    const jaula = chartData?.find(j => j.name === displayedJaula);
    return rateDays(jaula).sort((a, b) => {
      const [da, ma, ya] = a.split('/').map(Number);
      const [db, mb, yb] = b.split('/').map(Number);
      return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
    });
  }, [chartData, displayedJaula]);

  const displayedDay = localDay || selectedDay || defaultDay || orderedDays.at(-1) || '';
  const jaulaLinea = activeJaulaObj || chartData?.[0];
  const effectiveRateKey = resolveRateKey(jaulaLinea, displayedDay, selectedRateKey);
  const rate = FEEDING_RATES[effectiveRateKey];
  const tasaObj = jaulaLinea?.[rate.graph]?.find((entry) => entry.day === displayedDay);
  // const activityObj  = pickByDayOrLast(jaulaLinea, "graph_activity", displayedDay);
  const perdidosObj  = pickByDayOrLast(jaulaLinea, "graph_perdidos", displayedDay);
  const alarmsObj    = pickByDayOrLast(jaulaLinea, "alarms_data",    displayedDay);
  const alarmIntervals = Array.isArray(alarmsObj?.value) ? alarmsObj.value : [];

  // Si compact === true â†’ graph_activity.data y mÃºltiples jaulas
  let activitySources = [];

  if (compact) {
    // Encuentra todas las claves que empiecen con graph_activity
    const keys = Object.keys(jaulaLinea || {}).filter(k => k.startsWith("graph_activity"));
    keys.forEach((k) => {
      const g = jaulaLinea[k];
      if (g && Array.isArray(g.data)) {
        const dayObj = g.data.find(d => d.day === displayedDay);
        if (dayObj) activitySources.push({ name: g.name || k, value: dayObj.value });
      }
    });
  } else {
    const normalObj = pickByDayOrLast(jaulaLinea, "graph_activity", displayedDay);
    if (normalObj) activitySources.push({ name: jaulaLinea.name, value: normalObj.value });
  }


  // Series [{x,y}]
  const lineDataPoints     = toLinePoints(tasaObj, true);
  // const lineDataPointsA    = toLinePoints(activityObj);
  const linePerdidosPoints = toLinePoints(perdidosObj);
  // En vista compacta ambas jaulas comparten ambiente: prioriza la primera que tenga oxígeno.
  const oxygenSource = firstOxygenSource(activitySources);
  const oxygenSo2Points = toMetricPoints(oxygenSource?.value, 'so2');
  const oxygenO2Points = toMetricPoints(oxygenSource?.value, 'o2');
  const hasOxygenSo2 = oxygenSo2Points.length > 0;
  const hasOxygenO2 = oxygenO2Points.length > 0;
  const hasOxygen = hasOxygenSo2 || hasOxygenO2;
  // Temperatura: misma lógica de ambiente compartido que el oxígeno.
  const tempSource = firstTempSource(activitySources);
  const tempPoints = toMetricPoints(tempSource?.value, 'temp');
  const hasTemp = tempPoints.length > 0;
  // Correntómetro: dirección (cd, grados) y velocidad (cs, cm/s) inline en graph_activity.
  const currentDirPoints = activitySources
    .flatMap((src) => (Array.isArray(src.value) ? src.value : []))
    .filter((point) => isPresent(pointMetric(point, 'cd')))
    .map((point) => ({
      x: parseDateLocal(point.time).getTime(),
      cd: Number(pointMetric(point, 'cd')),
      cs: isPresent(pointMetric(point, 'cs')) ? Number(pointMetric(point, 'cs')) : null,
    }));
  const hasCurrent = currentDirPoints.length > 0;
  const currentSamples = sampleCurrentDirections(currentDirPoints);
  const renderedOxygenMode = oxygenMode !== 'hidden' && (
    (oxygenMode === 'so2' && hasOxygenSo2) || (oxygenMode === 'o2' && hasOxygenO2)
  ) ? oxygenMode : (hasOxygenSo2 ? 'so2' : 'o2');

  const greenPalette = [
    '#00FF99', // Verde neÃ³n
    '#00CC66', // Verde saturado
    '#00994D', // Verde medio profundo
    '#66FFB2', // Verde agua
    '#33CC99'  // Verde jade
  ];

  //  Crear datasets dinÃ¡micos para cada graph_activity
  const activityDatasets = activitySources.map((src, idx) => {
    const color = greenPalette[idx % greenPalette.length];
    return {
      label: compact
        ? `${t('activity_abbr', 'Act')} ${src.name ?? idx + 1} [ % ]`
        : `${t('activity', 'Actividad')} [ % ]`,
      tooltipKey: 'activity',
      metricKey: 'activity',
      data: (Array.isArray(src.value) ? src.value.map(item => ({
        ...item,
        x: parseDateLocal(item.time).getTime(),
        y: item.value
      })) : []),
      tension: 0.35,
      borderColor: color,
      textColor: color,
      yAxisID: 'yAxis1',
      visibilityKey: `activity:${src.name ?? idx}`,
      ...lineGraphConfig,
    };
  });

  const alarmPoints = alarmIntervals.map((alarm) => {
    const presentation = getAlarmPresentation(alarm, showAlarmReaction);
    return {
      x: parseDateLocal(alarm.start).getTime(),
      y: 50,
      alarmData: alarm,
      alarmColor: presentation.color,
      markerColor: presentation.markerColor,
      reactionEndX: getAlarmReactionEnd(alarm, showAlarmReaction),
      pointStyle: presentation.icon === 'important'
        ? notificationImportantPointStyle
        : notificationPointStyle,
    };
  });
  const notePoints = useMemo(
    () => buildNotePoints(
      notesByJaulaDay?.[displayedJaula]?.[normalizeNoteDayKey(displayedDay)]
    ),
    [displayedDay, displayedJaula, notesByJaulaDay],
  );

  // Rango original X
  // const originalXMin =
  //   lineDataPoints.length > 0 ? lineDataPoints[0].x
  //     : (lineDataPointsA.length > 0 ? lineDataPointsA[0].x
  //     : (linePerdidosPoints[0]?.x ?? null));
  // const originalXMax =
  //   lineDataPoints.length > 0 ? lineDataPoints[lineDataPoints.length - 1].x
  //     : (lineDataPointsA.length > 0 ? lineDataPointsA[lineDataPointsA.length - 1].x
  //     : (linePerdidosPoints[linePerdidosPoints.length - 1]?.x ?? null));
  const visibleXValues = [
    ...lineDataPoints,
    ...activityDatasets.flatMap((dataset) => dataset.data || []),
    ...linePerdidosPoints,
    ...notePoints,
  ].map((point) => point.x).filter(Number.isFinite);
  const originalXMin = visibleXValues.length ? Math.min(...visibleXValues) : null;
  const originalXMax = visibleXValues.length ? Math.max(...visibleXValues) : null;


  originalXRef.current = { min: originalXMin, max: originalXMax };

  // Unidades para tÃ­tulo
  const units = rate.unit;
  const missingRateText = selectedRateKey === 'auto'
    ? t('feeding_rate_no_data', 'Sin datos de tasa')
    : t('feeding_rate_no_data_unit', 'Sin datos de {unit}').replace('{unit}', units);

  // ======= Datasets combinados (memoizados) =======
  const combinedLineChartData = useMemo(() => ({
    datasets: [
      ...activityDatasets,
      ...(hasOxygen ? [{
        label: `${t('oxygen', 'Oxígeno')} [ ${renderedOxygenMode === 'so2' ? '%' : 'mg/L'} ]`,
        tooltipKey: 'oxygen',
        visibilityKey: 'oxygen',
        isOxygen: true,
        metricKey: renderedOxygenMode,
        data: renderedOxygenMode === 'so2' ? oxygenSo2Points : oxygenO2Points,
        hidden: oxygenMode === 'hidden',
        tension: 0.35,
        borderColor: cyanLine,
        textColor: cyanLine,
        yAxisID: 'yAxisO2',
        ...lineGraphConfig,
      }] : []),
      ...(hasTemp ? [{
        label: `${t('temperature', 'Temperatura')} [ °C ]`,
        tooltipKey: 'temperature',
        visibilityKey: 'temperature',
        metricKey: 'temp',
        data: tempPoints,
        tension: 0.35,
        borderColor: tempLine,
        textColor: tempText,
        yAxisID: 'yAxisTemp',
        ...lineGraphConfig,
      }] : []),
      {
        label: `${t('feeding_rate_short', 'Tasa')} [ ${units} ]`,
        tooltipKey: 'feeding_rate',
        visibilityKey: 'feeding_rate',
        metricKey: effectiveRateKey,
        data: lineDataPoints,
        backgroundColor: orangeBG,
        borderColor: orangeLine,
        yAxisID: 'yAxis2',
        textColor: 'rgba(232, 78, 14, 0.85)',
        ...areaGraphConfig,
      },
      {
        label: t('ppa', 'PPA'),
        tooltipKey: 'ppa',
        visibilityKey: 'ppa',
        metricKey: 'perdidos',
        data: linePerdidosPoints,
        tension: 0.25,
        borderColor: redLine,
        yAxisID: 'yAxis3',
        textColor: 'rgba(172, 20, 20, 0.85)',
        ...lineGraphConfig,
      },
      {
        label: t('alarms', 'Alarmas'),
        tooltipKey: 'alarms',
        visibilityKey: 'alarms',
        type: 'scatter',
        data: alarmPoints,
        showLine: false,
        pointStyle: ({ raw }) => raw?.pointStyle || notificationPointStyle,
        radius: 5,
        pointHoverRadius: 10,
        hitRadius: 16,
        pointHitRadius: 16,
        backgroundColor: ({ raw }) => raw?.markerColor || yellow,
        yAxisID: 'yAxis1',
        textColor: yellowText,
        isAlarm: true,
      },
      {
        label: t('notes_chart_label', 'Notas'),
        tooltipKey: 'notes',
        visibilityKey: 'notes',
        type: 'scatter',
        data: notePoints,
        showLine: false,
        pointStyle: notePointStyle,
        radius: 6,
        pointHoverRadius: 10,
        hitRadius: 12,
        pointHitRadius: 12,
        backgroundColor: noteColor,
        borderColor: '#d8cdf8',
        borderWidth: 1,
        yAxisID: 'yAxis1',
        textColor: noteColor,
        isNote: true,
      },
    ],
  }), [
    // ðŸ”¸ Solo se recalcula si cambian estos valores reales:
    JSON.stringify(activityDatasets),
    JSON.stringify(lineDataPoints),
    JSON.stringify(linePerdidosPoints),
    JSON.stringify(alarmPoints),
    JSON.stringify(oxygenO2Points),
    JSON.stringify(oxygenSo2Points),
    JSON.stringify(tempPoints),
    JSON.stringify(notePoints),
    hasOxygen,
    hasTemp,
    oxygenMode,
    renderedOxygenMode,
    units,
    effectiveRateKey,
    translate,
    notificationPointStyle,
    notePointStyle,
  ]);

  const handleLegendClick = useCallback((event, legendItem, legend) => {
    const dataset = legend.chart.data.datasets[legendItem.datasetIndex];
    if (!dataset?.isOxygen) {
      Chart.defaults.plugins.legend.onClick(event, legendItem, legend);
      return;
    }
    setOxygenMode((mode) => nextOxygenMode(mode, hasOxygenSo2, hasOxygenO2));
  }, [hasOxygenO2, hasOxygenSo2]);

  // const combinedLineChartData = {
  //   datasets: [
  //     {
  //       label: 'Actividad [ % ]',
  //       data: lineDataPointsA,
  //       tension: 0.4,
  //       borderColor: greenLine,
  //       yAxisID: 'yAxis1',
  //       textColor: 'rgba(85, 187, 69, 0.85)',
  //       ...lineGraphConfig,
  //     },
  //     {
  //       label: `Tasa [ ${units} ]`,
  //       data: lineDataPoints,
  //       backgroundColor: orangeBG,
  //       borderColor: orangeLine,
  //       yAxisID: 'yAxis2',
  //       textColor: 'rgba(232, 78, 14, 0.85)',
  //       ...areaGraphConfig,
  //     },
  //     {
  //       label: 'PPA',
  //       data: linePerdidosPoints,
  //       tension: 0.25,
  //       borderColor: redLine,
  //       yAxisID: 'yAxis3',
  //       textColor: 'rgba(172, 20, 20, 0.85)',
  //       ...lineGraphConfig,
  //     },
  //     {
  //       label: 'Alarmas',
  //       type: 'scatter',
  //       data: alarmPoints,
  //       showLine: false,
  //       pointStyle: 'rectRot',
  //       radius: 5,
  //       pointHoverRadius: 10,
  //       backgroundColor: yellow,
  //       yAxisID: 'yAxis1',
  //       textColor: 'rgba(255, 206, 86, 0.85)',
  //     },
  //   ],
  // };

  // Sincroniza props -> estado local
  useEffect(() => { if (jaulaSeleccionada !== localJaula) setLocalJaula(jaulaSeleccionada); }, [jaulaSeleccionada]);
  useEffect(() => { if (selectedDay !== localDay) setLocalDay(selectedDay); }, [selectedDay]);

  // ---------- POPPER (MUI) como tooltip ----------
  const popperAnchorRef = useRef({
    getBoundingClientRect: () => new DOMRect(),
    contextElement: null
  });

  const hideTimerRef = useRef(null);
  const hideScheduledRef = useRef(false);
  const tooltipLockedRef = useRef(false);

    // refs al contenedor que envuelve leyenda + canvas
  const chartBlockRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [darkModePDF, setDarkModePDF] = useState(false);
  const handleOpenDialog = () => setDialogOpen(true);

  const handleExport = async () => {

    try {
      // 1) estado de carga + deshabilitar botÃ³n externo
      setLoadingPDF(true);
      setProgressPDF(0);
      // 2) cerrar el modal de inmediato (lo pides explÃ­citamente)
      setDialogOpen(false);

      await exportToPDF({
        chart: lineChartInstanceRef.current,
        darkModePDF,
        title: `${t('feeding_rate', 'Detalle Diario')} [ ${units} ]`,
        jaula: displayedJaula,
        dia: displayedDay,
        onProgress: (pct) => setProgressPDF(Number.isFinite(pct) ? pct : 0),
      });

    } finally {
      // 5) restaurar estados
      setLoadingPDF(false);
      setProgressPDF(0);
    }
  };

  const [popperOpen, setPopperOpen] = useState(false);
  const [popperContent, setPopperContent] = useState({
    title: '',
    items: [],
    alarmBlock: null,
    noteBlock: null,
    placement: 'top',
  });

  // external tooltip handler â†’ construye contenido y posiciona Popper
  const externalTasaHandler = useCallback((context) => {
    const { chart, tooltip } = context;
    if (!chart || chart._destroyed || !chart.canvas) return;

    // Construye los puntos mÃ¡s cercanos
    const nearest = buildNearestPerDataset(tooltip);

    // ðŸ” Filtrar datasets visibles antes de activar puntos
    const visibleNearest = nearest.filter(dp => {
      const ds = chart.data.datasets[dp.datasetIndex];
      const meta = chart.getDatasetMeta(dp.datasetIndex);
      const isHidden = ds.hidden || meta?.hidden;
      if (isHidden) {
        console.log(`âš ï¸ Tooltip intentÃ³ activar dataset oculto: ${ds.label || `#${dp.datasetIndex}`}`);
      }
      return !isHidden;
    });

    const key = visibleNearest.map(dp => `${dp.datasetIndex}:${dp.dataIndex}`).join('|');

    if (chart.$lastActiveKey !== key) {
      chart.$lastActiveKey = key;

      // âœ… Solo activar elementos de datasets visibles
      chart.setActiveElements(
        visibleNearest.map(dp => ({ datasetIndex: dp.datasetIndex, index: dp.dataIndex }))
      );

      chart.update('none');
    }

    // Si no hay puntos visibles, ocultar tooltip
    if (!visibleNearest.length || tooltip.opacity === 0) {
      if (!tooltipLockedRef.current) setPopperOpen(false);
      return;
    }

    // === Posicionamiento del tooltip (Popper) ===
    const rect = chart.canvas.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const preferBottom = spaceBelow >= spaceAbove;
    const anchorY = preferBottom ? rect.bottom + 8 : rect.top - 8;
    const pageX = centerX + window.pageXOffset;
    const pageY = anchorY + window.pageYOffset;

    popperAnchorRef.current.getBoundingClientRect = () => ({
      x: pageX,
      y: pageY,
      left: pageX,
      right: pageX,
      top: pageY,
      bottom: pageY,
      width: 0,
      height: 0,
      toJSON: () => {},
    });

    // === TÃ­tulo del tooltip (timestamp) ===
    let title = '';
    const ts = tooltip.title?.[0];
    if (ts) {
      const d = new Date(Number(ts));
      if (!isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        title = `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
      } else {
        title = ts;
      }
    }

    const alarmDp = visibleNearest.find((dp) => {
      const ds = chart.data.datasets[dp.datasetIndex];
      return ds?.tooltipKey === 'alarms'
        && getPointXDistancePx(chart, dp) <= getAlarmTooltipTolerancePx(ds);
    });
    const alarmData = alarmDp?.raw?.alarmData;
    const exactAlarmSnapshot = alarmData?.snapshot;

    // === Items visibles del tooltip ===
    const items = visibleNearest
      .filter(dp => {
        const dataset = chart.data.datasets[dp.datasetIndex];
        return dataset?.tooltipKey !== 'alarms' && !dataset?.isNote;
      })
      .map(dp => {
        const ds = chart.data.datasets[dp.datasetIndex];
        const exactValue = exactAlarmSnapshot && ds?.metricKey
          ? pointMetric(alarmData, ds.metricKey)
          : null;
        const val = isPresent(exactValue) ? Number(exactValue) : dp.parsed?.y;
        const txt = typeof val === 'number'
          ? (Number.isInteger(val) ? val : Number(val).toFixed(2))
          : (val ?? '');
        const clr = ds.textColor || ds.borderColor || ds.backgroundColor || '#cfcfcf';
        return {
          label: tooltipMetricLabel(
            ds?.label ?? '',
            ds?.metricKey,
            isPresent(exactValue),
            t('analytics_instantaneous', 'Instantánea'),
          ),
          value: txt,
          color: Array.isArray(clr) ? clr[dp.dataIndex] : clr,
          metricKey: ds?.metricKey,
        };
      });

    if (exactAlarmSnapshot) {
      [
        {
          key: renderedOxygenMode,
          label: `${t('oxygen', 'Oxígeno')} [ ${renderedOxygenMode === 'so2' ? '%' : 'mg/L'} ]`,
          color: cyanLine,
        },
        { key: 'temp', label: `${t('temperature', 'Temperatura')} [ °C ]`, color: tempText },
      ].forEach(({ key, label, color }) => {
        const value = pointMetric(alarmData, key);
        if (isPresent(value) && !items.some(item => item.metricKey === key)) {
          items.push({ label, value: formatMetric(value), color, metricKey: key });
        }
      });
    }

    const currentPoint = exactAlarmSnapshot
      ? alarmData
      : visibleNearest
        .filter(({ raw }) => isPresent(pointMetric(raw, 'cs')) || isPresent(pointMetric(raw, 'cd')))
        .sort((left, right) => left.distance - right.distance)[0]?.raw;
    if (currentPoint && (isPresent(pointMetric(currentPoint, 'cs')) || isPresent(pointMetric(currentPoint, 'cd')))) {
      const parts = [];
      if (isPresent(pointMetric(currentPoint, 'cs'))) parts.push(`${formatMetric(pointMetric(currentPoint, 'cs'))} cm/s`);
      if (isPresent(pointMetric(currentPoint, 'cd'))) parts.push(`${formatMetric(pointMetric(currentPoint, 'cd'), 0)}°`);
      items.push({ label: t('current', 'Corriente'), value: parts.join(' · '), color: currentColor });
    }

    const depthPoint = exactAlarmSnapshot
      ? (isPresent(pointMetric(alarmData, 'depth')) ? { raw: alarmData } : null)
      : visibleNearest
        .filter(({ raw }) => isPresent(pointMetric(raw, 'depth')))
        .sort((left, right) => left.distance - right.distance)[0];
    if (depthPoint) {
      items.push({
        label: t('depth', 'Profundidad'),
        value: `${formatMetric(pointMetric(depthPoint.raw, 'depth'))} m`,
        color: depthPoint.dataset?.textColor || orangeLine,
      });
    }

    const horizontalPositionPoint = exactAlarmSnapshot
      ? (isPresent(pointMetric(alarmData, 'horizontal_position')) ? { raw: alarmData } : null)
      : visibleNearest
        .filter(({ raw }) => isPresent(pointMetric(raw, 'horizontal_position')))
        .sort((left, right) => left.distance - right.distance)[0];
    if (horizontalPositionPoint) {
      items.push({
        label: t('horizontal_position', 'Posición horizontal'),
        value: `${formatMetric(pointMetric(horizontalPositionPoint.raw, 'horizontal_position'))} m`,
        color: horizontalPositionPoint.dataset?.textColor || orangeLine,
      });
    }

    // === Bloque de alarmas ===
    let alarmBlock = null;
    if (alarmDp) {
      const date = new Date(alarmDp.raw?.alarmData?.start);
      const startTime = date.toLocaleTimeString('es-ES', {
        timeZone: 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      alarmBlock = {
        startTime,
        duration: formatAlarmDuration(alarmDp.raw?.alarmData?.duration),
        detectMax: alarmDp.raw?.alarmData?.detectados_max ?? '-',
        detectTot: alarmDp.raw?.alarmData?.detectados_total ?? '-',
        alarmData,
        preContext: buildPreAlarmContextPresentation(alarmData?.pre_alarm_context),
        color: alarmDp.raw?.alarmColor || chart.data.datasets[alarmDp.datasetIndex].textColor || '#cfcfcf',
      };
    }

    let noteBlock = null;
    const noteDp = visibleNearest.find((dp) => {
      const dataset = chart.data.datasets[dp.datasetIndex];
      return dataset?.isNote && getPointXDistancePx(chart, dp) <= getAlarmTooltipTolerancePx(dataset);
    });
    if (noteDp) noteBlock = noteDp.raw?.noteData || null;

    // === Determinar placement (top o bottom) ===
    const placement = preferBottom ? 'bottom' : 'top';

    // ðŸ”¹ Mostrar tooltip final
    setPopperContent({ title, items, alarmBlock, noteBlock, placement });
    setPopperOpen(true);

  }, [renderedOxygenMode, showAlarmReaction, translate]);


  // InstanciaciÃ³n del chart
  useEffect(() => {
    if (!lineChartRef.current) return;

    const chart = new Chart(lineChartRef.current, {
      type: 'line',
      data: combinedLineChartData,
      plugins: [alarmReactionPlugin, hoverColumnPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        animation: { duration: 0 },
        elements: { point: { radius: 2, hoverRadius: 6, hitRadius: 10 } },
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              boxWidth: window.innerWidth * 0.01,
              font: { size: fontSizeEjes },
              generateLabels: generateTasaLegendLabels,
            },
            onClick: handleLegendClick,
          },
         
          tooltip: {
            enabled: false,
            external: (ctx) => {
              if (typeof window !== 'undefined') {
                if (externalTasaHandler.raf) cancelAnimationFrame(externalTasaHandler.raf);
                externalTasaHandler.raf = requestAnimationFrame(() => externalTasaHandler(ctx));
                if (externalTasaHandler.raf) cancelAnimationFrame(externalTasaHandler.raf);
                 externalTasaHandler.raf = requestAnimationFrame(() => {
                   // chart pudo ser destruido entre frames
                   const { chart } = ctx;
                   if (!chart || chart._destroyed || !chart.canvas) return;
                   externalTasaHandler(ctx);
                 });
              } else {
                externalTasaHandler(ctx);
              }
            },
            callbacks: {
              title: (tooltipItem) => {
                const timestamp = tooltipItem[0].parsed.x;
                return new Date(timestamp).toLocaleString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                });
              },
              label: (tooltipItem) => {
                const label = tooltipItem.dataset.label || '';
                const value = tooltipItem.formattedValue ?? tooltipItem.parsed?.y;
                return `${label}: ${value}`;
              },
            }
          },
          zoom: {
            limits: { x: { min: originalXRef.current.min ?? undefined, max: originalXRef.current.max ?? undefined } },
            pan: { enabled: true, mode: 'x', modifierKey: null },
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, drag: { enabled: true, modifierKey: 'ctrl' }, mode: 'x' }
          },
        },
        scales: {
          x: {
            type: 'linear',
            min: originalXRef.current.min,
            max: originalXRef.current.max,
            title: { display: true, text: t('time', 'Tiempo') },
            ticks: {
              font: { size: fontSizeEjes },
              callback: (value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
              color: '#C3C3C3',
            },
            grid: { color: colorLine, lineWidth: borderWidth },
          },
          yAxis1: { type: 'linear', min: 0, max: 100, position: 'left', title: { display: false }, ticks: { font: { size: fontSizeEjes }, color: greenLine }, grid: { color: colorLine, lineWidth: borderWidth } },
          yAxisO2: { type: 'linear', min: 0, suggestedMax: renderedOxygenMode === 'so2' ? 120 : undefined, position: 'left', display: hasOxygen && oxygenMode !== 'hidden', title: { display: false }, ticks: { font: { size: fontSizeEjes }, color: cyanLine }, grid: { drawOnChartArea: false } },
          yAxis2: { type: 'linear', position: 'right', title: { display: false, text: `${t('feeding_rate_short', 'Tasa')} [${units}]` }, min: 0, ticks: { font: { size: fontSizeEjes }, color: orangeLine }, grid: { color: 'rgba(255, 255, 255, 0)', lineWidth: borderWidth } },
          yAxis3: { type: 'linear', position: 'right', offset: false, title: { display: false }, min: 0, ticks: { font: { size: fontSizeEjes }, color: redText }, grid: { drawOnChartArea: false } },
          yAxisTemp: buildTemperatureScale(fontSizeEjes, hasTemp),
        },
      },
    });

    // Guarda instancia y datos auxiliares (tooltip externo)
    lineChartInstanceRef.current = chart;
    chart._alarmIntervals = alarmIntervals;

    // mouse pos para tooltip externo (opcional)
    const canvas = lineChartRef.current;
    const onMove = (e) => { chart._tooltipMouseX = e.clientX; chart._tooltipMouseY = e.clientY; };
    canvas.addEventListener('mousemove', onMove);

    return () => {
      canvas.removeEventListener('mousemove', onMove);
       if (externalTasaHandler.raf) {
       cancelAnimationFrame(externalTasaHandler.raf);
       externalTasaHandler.raf = null;
     }
      chart.destroy();
      lineChartInstanceRef.current = null;
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideScheduledRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jaulaSeleccionada, selectedDay, chartData, localJaula, localDay, units, fontSizeEjes, translate]);

// ======= useEffect para actualizar chart sin recargar =======
useEffect(() => {
  [lineChartInstanceRef.current, modalChartRef.current].filter(Boolean).forEach((chart) => {
    const hiddenKeys = new Set(
      chart.data.datasets
        .filter((_dataset, index) => !chart.isDatasetVisible(index))
        .map((dataset) => dataset.visibilityKey || dataset.label)
    );

    chart.data = combinedLineChartData;
    chart.data.datasets.forEach((dataset, index) => {
      chart.getDatasetMeta(index).hidden = dataset.isOxygen
        ? null
        : (hiddenKeys.has(dataset.visibilityKey || dataset.label) ? true : null);
    });

    const oxygenScale = chart.options.scales?.yAxisO2;
    if (oxygenScale) {
      oxygenScale.display = hasOxygen && oxygenMode !== 'hidden';
      oxygenScale.suggestedMax = renderedOxygenMode === 'so2' ? 120 : undefined;
    }
    const tempScale = chart.options.scales?.yAxisTemp;
    if (tempScale) tempScale.display = hasTemp;
    chart.update('none');
  });
}, [combinedLineChartData, hasOxygen, hasTemp, oxygenMode, renderedOxygenMode]);

  // Reset zoom con Ã­cono Home
  const resetZoom = () => lineChartInstanceRef.current?.resetZoom();

  // Al cambiar jaula o dÃ­a, refresca lÃ­mites y resetea
  useEffect(() => {
    const chart = lineChartInstanceRef.current;
    if (!chart) return;
    const { min, max } = originalXRef.current;
    chart.options.plugins.zoom.limits.x.min = min ?? undefined;
    chart.options.plugins.zoom.limits.x.max = max ?? undefined;
    chart.options.scales.x.min = min;
    chart.options.scales.x.max = max;
    chart.resetZoom();
    chart.update('none');
  }, [displayedJaula, displayedDay, notePoints]);

  // Rueda mouse: sincroniza Ã­ndices con listas
  useEffect(() => {
    if (!orderedJaulas.length) { setActiveJaulaIndex(0); return; }
    const idx = orderedJaulas.findIndex(x => norm(x.name) === norm(localJaula ?? chartData?.[0]?.name));
    setActiveJaulaIndex(idx === -1 ? 0 : idx);
  }, [orderedJaulas, localJaula, chartData]);

  const selectJaulaByIndex = useCallback((i) => {
    if (!orderedJaulas.length) return;
    const idx = mod(i, orderedJaulas.length);
    setActiveJaulaIndex(idx);
    setLocalJaula?.(orderedJaulas[idx].name);
  }, [orderedJaulas]);

  const selectJaulaByName = useCallback((name) => {
    const idx = orderedJaulas.findIndex(x => norm(x.name) === norm(name));
    if (idx !== -1) setActiveJaulaIndex(idx);
    setLocalJaula?.(name);
  }, [orderedJaulas]);

  useEffect(() => {
    if (!orderedDays.length) { setActiveDayIndex(0); return; }
    const idx = orderedDays.findIndex(d => d === (localDay || defaultDay || ''));
    setActiveDayIndex(idx === -1 ? Math.max(0, orderedDays.length - 1) : idx);
  }, [orderedDays, localDay, defaultDay]);

  const selectDayByIndex = useCallback((i) => {
    if (!orderedDays.length) return;
    const idx = mod(i, orderedDays.length);
    setActiveDayIndex(idx);
    setLocalDay?.(orderedDays[idx]);
    setDefaultDay(orderedDays[idx]);
  }, [orderedDays]);

  const selectDayByValue = useCallback((day) => {
    const idx = orderedDays.findIndex(d => d === day);
    if (idx !== -1) setActiveDayIndex(idx);
    setLocalDay?.(day);
    setDefaultDay(day);
  }, [orderedDays]);


  // ================== CHART MODAL ==================
  const createModalChart = () => {
    if (!expandedCanvasRef.current) return;

    // Si existe, destruye antes de crear uno nuevo
    if (modalChartRef.current) {
      modalChartRef.current.destroy();
      modalChartRef.current = null;
    }

    modalChartRef.current = new Chart(expandedCanvasRef.current.getContext('2d'), {
      type: 'line',
      data: combinedLineChartData,
      plugins: hasCurrent
        ? [alarmReactionPlugin, hoverColumnPlugin, currentDirectionPlugin]
        : [alarmReactionPlugin, hoverColumnPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        animation: { duration: 0 },
        elements: { point: { radius: 2, hoverRadius: 6, hitRadius: 10 } },
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        plugins: {
          currentDirection: hasCurrent ? { samples: currentSamples, band: CURRENT_ARROW_BAND, length: CURRENT_ARROW_LEN } : undefined,
          legend: {
            position: 'top',
            labels: {
              boxWidth: window.innerWidth * 0.01,
              font: { size: fontSizeEjes },
              generateLabels: generateTasaLegendLabels,
            },
            onClick: handleLegendClick,
          },
          tooltip: {
            enabled: false,
            external: (ctx) => {
              if (typeof window !== 'undefined') {
                if (externalTasaHandler.raf) cancelAnimationFrame(externalTasaHandler.raf);
                externalTasaHandler.raf = requestAnimationFrame(() => externalTasaHandler(ctx));
                if (externalTasaHandler.raf) cancelAnimationFrame(externalTasaHandler.raf);
                externalTasaHandler.raf = requestAnimationFrame(() => {
                  const { chart } = ctx;
                  if (!chart || chart._destroyed || !chart.canvas) return;
                  externalTasaHandler(ctx);
                });
              } else {
                externalTasaHandler(ctx);
              }
            },
            callbacks: {
              title: (tooltipItem) => {
                const timestamp = tooltipItem[0].parsed.x;
                return new Date(timestamp).toLocaleString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                });
              },
              label: (tooltipItem) => {
                const label = tooltipItem.dataset.label || '';
                const value = tooltipItem.formattedValue ?? tooltipItem.parsed?.y;
                return `${label}: ${value}`;
              },
            }
          },
          zoom: {
            limits: { x: { min: originalXRef.current.min ?? undefined, max: originalXRef.current.max ?? undefined } },
            pan: { enabled: true, mode: 'x', modifierKey: null },
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, drag: { enabled: true, modifierKey: 'ctrl' }, mode: 'x' }
          },
        },
        scales: {
          x: {
            type: 'linear',
            min: originalXRef.current.min,
            max: originalXRef.current.max,
            title: { display: !hasCurrent, text: t('time', 'Tiempo') },
            // Reserva una franja bajo el eje (entre ticks y borde) para las flechas/brujula de corriente.
            afterFit: hasCurrent ? (scale) => { scale.height += CURRENT_ARROW_BAND; } : undefined,
            ticks: {
              font: { size: fontSizeEjes },
              callback: (value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
              color: '#C3C3C3',
            },
            grid: { color: colorLine, lineWidth: borderWidth },
          },
          yAxis1: { type: 'linear', min: 0, max: 100, position: 'left', title: { display: false }, ticks: { font: { size: fontSizeEjes }, color: greenLine }, grid: { color: colorLine, lineWidth: borderWidth } },
          yAxisO2: { type: 'linear', min: 0, suggestedMax: renderedOxygenMode === 'so2' ? 120 : undefined, position: 'left', display: hasOxygen && oxygenMode !== 'hidden', title: { display: false }, ticks: { font: { size: fontSizeEjes }, color: cyanLine }, grid: { drawOnChartArea: false } },
          yAxis2: { type: 'linear', position: 'right', title: { display: false, text: `${t('feeding_rate_short', 'Tasa')} [${units}]` }, min: 0, ticks: { font: { size: fontSizeEjes }, color: orangeLine }, grid: { color: 'rgba(255, 255, 255, 0)', lineWidth: borderWidth } },
          yAxis3: { type: 'linear', position: 'right', offset: false, title: { display: false }, min: 0, ticks: { font: { size: fontSizeEjes }, color: redText }, grid: { drawOnChartArea: false } },
          yAxisTemp: buildTemperatureScale(fontSizeEjes, hasTemp),
        },
      },
    });
  };

  const destroyModalChart = () => {
    if (modalChartRef.current) {
      modalChartRef.current.destroy();
      modalChartRef.current = null;
    }
  };

  // Re-crea el chart del modal cuando cambie el dÃ­a/jaula estando abierto
  useEffect(() => {
    if (!expanded) return;
    createModalChart(); // se destruye y crea con data+lÃ­mites frescos
  }, [expanded, displayedJaula, displayedDay]); // <- claves para el modal


  return (
    <Grid item xs={6} style={{ position: "relative", width: "100%", height: "100%" }}>

      {loadingPDF && progressPDF > 0 && (
        <Box sx={{ width: '17%', mt: '1vh',marginLeft:'17.2vw', position:'absolute' }}>
          <LinearProgress
            variant="determinate"
            value={progressPDF}
            // sx={{ height: 8, borderRadius: 2 }}
            sx={{
                height: '.8vh',
                borderRadius: '.5vw',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#e94e1a',   // color de la barra
                },
                backgroundColor: 'rgba(233,78,26,0.2)' // opcional, color del fondo
              }}
          />
          <p  sx={{ mt: '.25vh', textAlign: "center" }}>
            {progressPDF}% {t('generating_pdf', 'Generando PDF...')}
          </p>
        </Box>
      )}

      <HomeIcon
        onClick={resetZoom}
        style={{ position: 'absolute', marginLeft: '1vw', marginTop: '1vh', fontSize: '1.3vw', color: '#c3c3c3' }}
      />

      {/* === Opciones de descarga y ampliar grafico === */}
      <div style={{ position: "relative" }}>
        <SpeedDial
          ariaLabel="Opciones"
          icon={
            <MoreVertIcon
              sx={{
                color: '#c3c3c3 !important',
                fontSize: '1vw !important',
              }}
            />
          }
          direction="down"
          FabProps={{
            sx: {
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
              width: '2vw !important',
              height: '2vw !important',
              background: 'rgba(0, 0, 0, 0)',
              boxShadow: 0,
              '&:hover': { background: 'rgba(0,0,0,0)' },
              marginBottom: '-2vh',
            },
          }}
          sx={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
            right: '0vw',
            top: '.3vh',
            zIndex: 10,
            color: '#c3c3c3 !important',
            fontSize: '1vw !important',
          }}
        >
          <SpeedDialAction
            key="expand"
            icon={
              <OpenInFullIcon
                className="icon-m"
                sx={{ fontSize: '.8vw !important' }}
              />
            }
            tooltipTitle={t('expand', 'Expandir')}
            onClick={() => setExpanded(true)}
            FabProps={{
              sx: {
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                background: 'rgba(0,0,0,0.95)',
                color: 'white',
                width: '1.5vw !important',
                height: '1.5vw !important',
                boxShadow: 'none',
                '&:hover': { background: 'rgba(0,0,0,0.8)' },
                marginBottom: '-.2vh',
              },
            }}
          />

          <SpeedDialAction
            key="download"
            icon={
              <FileDownloadIcon
                className="icon-m"
                sx={{ fontSize: '.8vw !important' }}
              />
            }
            tooltipTitle={t('download', 'Descargar')}
            onClick={handleOpenDialog}
            FabProps={{
              sx: {
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                background: 'rgba(0,0,0,0.95)',
                color: 'white',
                width: '1.5vw !important',
                height: '1.5vw !important',
                boxShadow: 'none',
                '&:hover': { background: 'rgba(0,0,0,0.8)' },
              },
            }}
          />
        </SpeedDial>


        {/* MODAL DE EXPORTACIÃ“N */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          PaperProps={{
            sx: {
              bgcolor: '#0c0c0c !important',
              color: "#e6e6e6",
              border: "0.01vw solid #e94e1a",
              borderRadius: ".4vw",
              minWidth: '15vw',
              boxShadow: 'none',
            },
          }}
          slotProps={{
            backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0)" } },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              letterSpacing: 0.2,
              borderBottom: "0.01vw solid #e94e1a",
              pb: "1vh",
              bgcolor: '#0c0c0c !important',
            }}
          >
            {t('export_options', 'Opciones de exportaciÃ³n')}
          </DialogTitle>

          <DialogContent sx={{  display: "grid", gap: "1.2rem", bgcolor: '#0c0c0c !important', }}>
            <FormControlLabel
              sx={{ ".MuiFormControlLabel-label": { color: "#cbd5e1", fontWeight: 500 }, gap: ".5vw", marginTop: "1vh", }}
              control={
                <ScappSwitch
                  isChecked={darkModePDF}
                  onToggle={(e, checked) => setDarkModePDF(typeof checked === "boolean" ? checked : e.target.checked)}
                  inputProps={{ "aria-label": t('dark_mode_pdf', 'Modo oscuro PDF') }}
                />
              }
              label={t('dark_mode_pdf', 'Modo oscuro PDF')}
            />
          </DialogContent>

          <DialogActions sx={{ borderTop: "1px solid #e84e0e", p: 2, gap: 1,  bgcolor: '#0c0c0c !important', }}>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={{ textTransform: "none", color: "#cbd5e1",bgcolor: "#0c0c0c !important", "&:hover": { bgcolor: "rgba(148,163,184,.08) !important" } }}
            >
              {t('cancel', 'Cancelar')}
            </Button>
            <Button variant="contained" onClick={handleExport}
              sx={{ textTransform: "none", color: "#cbd5e1",bgcolor: "#0c0c0c !important", border:'solid 0.01vw #e84e0e', "&:hover": { bgcolor: "rgba(148,163,184,.08) !important" } }}
            >
              {t('generate_pdf', 'Generar PDF')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <div className="card" style={cardStyles.large}>
        {/* TÃ­tulo y selector */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '88%' }}>
          <h2 className='h2' style={{ marginTop: '-.2vh' }}>
            {t('feeding_rate', 'Detalle Diario')}
            {!lineDataPoints.length && <Box component="span" role="status" sx={{ display: 'block', color: '#aaa', fontSize: '0.75rem', fontWeight: 400 }}>{missingRateText}</Box>}
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            {/* Jaula */}
            <WheelPeekTrigger
              value={displayedJaula}
              valueIndex={activeJaulaIndex}
              items={orderedJaulas.map(x => x.name)}
              onChange={selectJaulaByName}
              onChangeIndex={selectJaulaByIndex}
              onOpen={handleOpenJaula}
              isMenuOpen={Boolean(anchorJaula)}
              textSx={{ fontSize: '.95em' }}
              peekDurationMs={1300}
            />
            <Menu anchorEl={anchorJaula} open={Boolean(anchorJaula)} onClose={handleCloseJaula} slotProps={menuStyle}>
              {orderedJaulas.map((j, idx) => (
                <MenuItem
                  key={`tasa-${j.name}-${j.srcIdx}`}
                  selected={idx === activeJaulaIndex}
                  onClick={() => { setActiveJaulaIndex(idx); setLocalJaula(j.name); handleCloseJaula(); }}
                  style={{ fontSize: '1.25vh' }}
                >
                  {j.label ?? j.name}
                </MenuItem>
              ))}
            </Menu>

            <h3 className='h3' style={{ margin: '0 0.4vw', color: 'rgba(255,255,255,0.47)', marginTop:'-.3vh' }}> {/* - */} </h3>

            {/* DÃ­a */}
            <WheelPeekTrigger
              prefix={""} //{` ${t('day', 'Dia')}: `}
              value={displayedDay}
              valueIndex={activeDayIndex}
              items={orderedDays}
              onChange={selectDayByValue}
              onChangeIndex={selectDayByIndex}
              onOpen={handleOpenDay}
              isMenuOpen={Boolean(anchorDay)}
              textSx={{ fontSize: '.95em' }}
              peekDurationMs={1300}
            />
            <Menu anchorEl={anchorDay} open={Boolean(anchorDay)} onClose={handleCloseDay} slotProps={menuStyle}>
              {orderedDays.map((day, idx) => (
                <MenuItem
                  key={`tasa-${day}-${idx}`}
                  selected={idx === activeDayIndex}
                  onClick={() => { setActiveDayIndex(idx); setLocalDay(day); handleCloseDay(); }}
                  style={{ fontSize: '1.25vh' }}
                >
                  {t('day', 'Dia')} {day}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>

        <div
          onDoubleClick={() => setExpanded(true)}
          style={{ height: '30vh', width: '39vw', cursor: 'pointer' }}
        >
          <canvas ref={lineChartRef} />
        </div>
      </div>

      {/* === MODAL EXPANDIDO === */}
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0c0c0c !important', 
            color: '#e0e0e0',
            minWidth: '81.5vw !important',
            height: '64.5vh',
            top: '16.2vh',
            right: '8vw',
            borderRadius: '0.4vw',
            boxShadow: 'none',
          },
        }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0)' } } }}
        TransitionProps={{
          onEntered: () => createModalChart(),
          onExited: () => destroyModalChart(),
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',  bgcolor: '#0c0c0c', }}>
          <h2 style={{ color: '#c3c3c3' }}>{t('feeding_rate', 'Detalle Diario')}
            {!lineDataPoints.length && <Box component="span" role="status" sx={{ display: 'block', fontSize: '0.85rem', fontWeight: 400 }}>{missingRateText}</Box>}
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            {/* Jaula (MODAL) */}
            <div
              onMouseEnter={() => setWheelZoomEnabled(false)}
              onMouseLeave={() => { if (!anchorDayModal && !anchorJaulaModal) setWheelZoomEnabled(true); }}
              onWheel={(e) => e.stopPropagation()}
            >
              <WheelPeekTrigger
                value={displayedJaula}
                valueIndex={activeJaulaIndex}
                items={orderedJaulas.map(x => x.name)}
                onChange={selectJaulaByName}
                onChangeIndex={selectJaulaByIndex}
                onOpen={handleOpenJaulaModal}
                isMenuOpen={Boolean(anchorJaulaModal)}
                textSx={{ fontSize: '.95em' }}
                peekDurationMs={1300}
              />
            </div>

            <Menu
              anchorEl={anchorJaulaModal}
              open={Boolean(anchorJaulaModal)}
              onClose={handleCloseJaulaModal}
              slotProps={menuStyle}
            >
              {orderedJaulas.map((j, idx) => (
                <MenuItem
                  key={`tasa-modal-${j.name}-${j.srcIdx}`}
                  selected={idx === activeJaulaIndex}
                  onClick={() => { setActiveJaulaIndex(idx); setLocalJaula(j.name); handleCloseJaulaModal(); }}
                  style={{ fontSize: '1.25vh' }}
                >
                  {j.label ?? j.name}
                </MenuItem>
              ))}
            </Menu>

            <h3 className='h3' style={{ margin: '0 0.4vw', color: 'rgba(255,255,255,0.47)', marginTop:'-.3vh' }}> - </h3>

            {/* DÃ­a (MODAL) */}
            <div
              onMouseEnter={() => setWheelZoomEnabled(false)}
              onMouseLeave={() => { if (!anchorDayModal && !anchorJaulaModal) setWheelZoomEnabled(true); }}
              onWheel={(e) => e.stopPropagation()}
            >
              <WheelPeekTrigger
                prefix={` ${t('day', 'Dia')}: `}
                value={displayedDay}
                valueIndex={activeDayIndex}
                items={orderedDays}
                onChange={selectDayByValue}
                onChangeIndex={selectDayByIndex}
                onOpen={handleOpenDayModal}
                isMenuOpen={Boolean(anchorDayModal)}
                textSx={{ fontSize: '.95em' }}
                peekDurationMs={1300}
              />
            </div>

            <Menu
              anchorEl={anchorDayModal}
              open={Boolean(anchorDayModal)}
              onClose={handleCloseDayModal}
              slotProps={menuStyle}
            >
              {orderedDays.map((day, idx) => (
                <MenuItem
                  key={`tasa-modal-${day}-${idx}`}
                  selected={idx === activeDayIndex}
                  onClick={() => { setActiveDayIndex(idx); setLocalDay(day); handleCloseDayModal(); }}
                  style={{ fontSize: '1.25vh' }}
                >
                  {t('day', 'Dia')} {day}
                </MenuItem>
              ))}
            </Menu>
          </div>
          <DialogActions>
            <Button onClick={() => setExpanded(false)} startIcon={<CloseFullscreenIcon style={{fontSize:'1vw', color:'#c3c3c3'}} />}>
            </Button>
          </DialogActions>
        </DialogTitle>

        <DialogContent dividers
          sx={{
            bgcolor: '#0c0c0c',
            color: '#e0e0e0',
          }}
        >
          <Box
            onDoubleClick={() => setExpanded(false)}
            sx={{ height: '52vh', width: '77vw', padding: '1vh 1vw' }}
          >
            <canvas ref={expandedCanvasRef} key={'expanded'} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Tooltip Popper (MUI) */}
      <Popper
        open={popperOpen}
        anchorEl={popperAnchorRef.current}
        // placement={popperContent.placement}
        placement={popperContent.placement} // ya serÃ¡ 'top' o 'bottom'
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
          { name: 'flip', enabled: true },
        ]}
        sx={{ zIndex: 1300, pointerEvents: 'auto' }}
      >
        <Box
          elevation={3}
          sx={{
            p: '0.5vw',
            maxHeight: '50vh',
            overflowY: 'auto',
            bgcolor: 'rgba(0,0,0,0.96)',
            color: '#c3c3c3',
            border: '0.01vw solid #e84e0e',
            borderRadius: '0.5vw',
            minWidth: 260,
          }}
          onMouseEnter={() => {
            tooltipLockedRef.current = true;
            if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; hideScheduledRef.current = false; }
          }}
          onMouseLeave={() => { tooltipLockedRef.current = false; setPopperOpen(false); }}
          onWheel={(e) => e.stopPropagation()}
        >
          {/* tÃ­tulo */}
          {popperContent.title ? (
            <div style={{ fontWeight: 800, fontSize: '.85vw', textAlign: 'center', marginBottom: '0.5vh' }}>{popperContent.title}</div>
          ) : null}

          {/* items (labels/valores) */}
          {popperContent.items.map((it) => (
            <div key={it.label} style={{
              display: 'grid', gridTemplateColumns: 'max-content auto',
              columnGap: '10px', alignItems: 'center', marginBottom: '0.5vh', fontSize: '.85rem'
            }}>
              <div style={{ color: it.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <strong style={{fontSize:'0.7vw'}}>{it.label}:</strong>
              </div>
              <div style={{ textAlign: 'right', color: '#a1a1a1ff', fontSize:'0.7vw' }}>{it.value}</div>
            </div>
          ))}

          {/* bloque Alarmas */}
          {popperContent.alarmBlock ? (
            <div style={{ marginTop: '0.5vh' }}>
              <div style={{ fontWeight: 800, color: popperContent.alarmBlock.color, marginBottom: '0.5vh', fontSize:'0.7vw' }}>
                {t('alarm', 'Alarm')}:
              </div>
              <ul style={{ margin: 0, paddingLeft: '2vw' }}>
                <li style={{ fontSize:'0.7vw' ,color: popperContent.alarmBlock.color,}}>{t('start', 'Start')}: <span style={{ color: '#a1a1a1ff', fontSize:'0.7vw' }}>{popperContent.alarmBlock.startTime}</span></li>
                <li style={{ fontSize:'0.7vw' ,color: popperContent.alarmBlock.color,}}>{t('duration', 'Duration')}: <span style={{ color: '#a1a1a1ff', fontSize:'0.7vw' }}>{popperContent.alarmBlock.duration} [s]</span></li>
                <li style={{ fontSize:'0.7vw' ,color: popperContent.alarmBlock.color,}}>{t('maximum_detections', 'Max detections')}: <span style={{ color: '#a1a1a1ff', fontSize:'0.7vw' }}>{popperContent.alarmBlock.detectMax}</span></li>
                <li style={{ fontSize:'0.7vw' ,color: popperContent.alarmBlock.color,}}>{t('total_detections', 'Total detections')}: <span style={{ color: '#a1a1a1ff', fontSize:'0.7vw' }}>{popperContent.alarmBlock.detectTot}</span></li>
                {showAlarmReaction ? (
                  <li style={{ fontSize:'0.7vw', color: popperContent.alarmBlock.color }}>
                    {t('analytics_alarm_reaction_time', 'Tiempo de reacción')}: <span style={{ color: '#a1a1a1ff', fontSize:'0.7vw' }}>{formatAlarmReaction(popperContent.alarmBlock.alarmData, t)}</span>
                  </li>
                ) : null}
              </ul>
              {popperContent.alarmBlock.preContext ? (
                <div style={{ marginTop: '0.6vh', paddingTop: '0.6vh', borderTop: '1px solid rgba(255,255,255,.2)', fontSize: '0.7vw' }}>
                  {popperContent.alarmBlock.preContext.insufficient ? (
                    <div>{t('analytics_previous_context_insufficient', 'Contexto previo: datos insuficientes')}</div>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: '0.3vh' }}>
                        {t('analytics_alarm_before_context', 'Antes de la alarma')} · {formatContextSpan(popperContent.alarmBlock.preContext.spanSec)} {t('analytics_previous', 'previos')}
                        {popperContent.alarmBlock.preContext.partial ? ` (${t('analytics_partial', 'parcial')})` : ''}
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '2vw' }}>
                        <li>{t('analytics_no_detections', 'Sin detecciones')}: {formatMetric(popperContent.alarmBlock.preContext.noDetectionPct)} %</li>
                        {popperContent.alarmBlock.preContext.positiveMedian != null ? (
                          <li>
                            {t('analytics_context_detections', 'Detecciones')}: {formatMetric(popperContent.alarmBlock.preContext.positiveMedian)} {t('analytics_typical', 'típico')}, {' '}
                            {formatMetric(popperContent.alarmBlock.preContext.positiveMax)} {t('analytics_maximum', 'máximo')}
                          </li>
                        ) : null}
                        <li>
                          {t('analytics_previous_alarm', 'Ya había alarma antes')}: {' '}
                          {popperContent.alarmBlock.preContext.hadPreviousAlarm ? t('si', 'Sí') : t('no', 'No')}
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {popperContent.noteBlock ? (
            <div style={{ marginTop: '0.5vh', borderTop: `1px solid ${noteColor}`, paddingTop: '0.5vh' }}>
              <div style={{ fontWeight: 800, color: noteColor, marginBottom: '0.4vh', fontSize: '0.7vw' }}>
                {t('notes_chart_label', 'Notas')}
              </div>
              <div style={{ color: '#d7d1e8', whiteSpace: 'pre-wrap', maxWidth: '22vw', fontSize: '0.7vw' }}>
                {popperContent.noteBlock.text}
              </div>
            </div>
          ) : null}
        </Box>
      </Popper>
    </Grid>
  );
};

export default TasaChart;

/* ===== estilos menÃº ===== */
const menuStyle = {
  paper: {
    sx: {
      backgroundColor: 'rgba(28,28,28)',
      maxHeight: '30vh',
      '& .MuiMenuItem-root': {
        color: 'rgba(255,255,255,0.78)',
        letterSpacing: '1.4',
        '&:hover': { backgroundColor: '#303030' },
        '&.Mui-selected': {
          backgroundColor: 'var(--border-color-opacity-lighter)',
          '&:hover': { backgroundColor: 'var(--border-color-opacity-disabled)' },
        },
      },
    },
    elevation: 0,
  },
};

/* ===== utilidades ===== */
const mod  = (n, m) => ((n % m) + m) % m;
const norm = (s) => (s ?? '').toString().trim().toLowerCase();

const parseDate = (isoString) => (!isoString.match(/([Z+-])/)) ? new Date(isoString + 'Z') : new Date(isoString);
function pickByDayOrLast(container, key, selectedDay) {
  const arr = container?.[key];
  if (!Array.isArray(arr) || arr.length === 0) return null;
  if (selectedDay) {
    const found = arr.find((item) => item.day === selectedDay);
    return found || null;
  }
  return arr[arr.length - 1];
}
function toLinePoints(obj, useLocal) {
  if (!obj || !Array.isArray(obj.value)) return [];
  const byTime = new Map(obj.value.map((item) => [item.time, item]));
  return obj.value.map((item) => ({
    ...item,
    ...(item.synthetic && byTime.has(item.source_time)
      ? { tooltipSource: byTime.get(item.source_time) }
      : {}),
    x: (useLocal ? parseDateLocal(item.time) : parseDate(item.time)).getTime(),
    y: item.value,
  }));
}

function toMetricPoints(points, key) {
  if (!Array.isArray(points)) return [];
  return points
    .filter((point) => isPresent(pointMetric(point, key)))
    .map((point) => ({ ...point, x: parseDateLocal(point.time).getTime(), y: Number(pointMetric(point, key)) }));
}

const isPresent = (value) => value !== undefined && value !== null && value !== '' && value !== '-'
  && typeof value !== 'boolean' && Number.isFinite(Number(value));
const formatMetric = (value, decimals = 2) => Number(value).toFixed(decimals).replace(/\.00$/, '');

function getAlarmTooltipTolerancePx(ds) {
  const hitRadius = Number(ds?.pointHitRadius ?? ds?.hitRadius ?? 0);
  const hoverRadius = Number(ds?.pointHoverRadius ?? ds?.radius ?? 0);
  return Math.max(18, hitRadius + hoverRadius);
}

function getPointXDistancePx(chart, dataPoint) {
  const xScale = chart?.scales?.x;
  const rawX = dataPoint?.parsed?.x ?? dataPoint?.raw?.x;
  if (!xScale || !Number.isFinite(rawX)) return Number.POSITIVE_INFINITY;

  const pointX = xScale.getPixelForValue(rawX);
  const mouseX = typeof chart._tooltipMouseX === 'number'
    ? chart._tooltipMouseX - chart.canvas.getBoundingClientRect().left
    : chart.tooltip?.caretX;

  return Number.isFinite(mouseX) ? Math.abs(pointX - mouseX) : Number.POSITIVE_INFINITY;
}

/* ===== nearest por dataset ===== */
function buildNearestPerDataset(tooltip) {
  // const { chart } = tooltip;
  // const scaleX = chart.scales.x;
  // const mouseX = chart._tooltipMouseX || 0;
  // const rect = chart.canvas.getBoundingClientRect();
  
  const chart = tooltip?.chart;
  if (!chart || chart._destroyed) return [];
  const canvas = chart.canvas;
  if (!canvas || !canvas.getBoundingClientRect) return [];
  const scaleX = chart.scales?.x;
  if (!scaleX || !scaleX.getValueForPixel) return [];
  const rect = canvas.getBoundingClientRect();
  const mouseX = typeof chart._tooltipMouseX === 'number' ? chart._tooltipMouseX : (rect.left + (tooltip?.caretX ?? 0));
  
  const relativeX = mouseX - rect.left;
  const xValue = scaleX.getValueForPixel(relativeX);
  
  if (xValue == null || Number.isNaN(xValue)) return [];
  
  const nearestPoints = [];
  // chart.data.datasets.forEach((ds, datasetIndex) => {
    // if (!Array.isArray(ds.data) || ds.data.length === 0) return;
    chart.data.datasets.forEach((ds, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      //  Evita incluir datasets ocultos
      if (ds.hidden || meta?.hidden) return;
      if (!Array.isArray(ds.data) || ds.data.length === 0) return;


    let best = null;
    ds.data.forEach((pt, dataIndex) => {
      const px = pt?.x ?? (pt.parsed ? pt.parsed.x : undefined);
      const py = pt?.y ?? (pt.parsed ? pt.parsed.y : undefined);
      if (px === undefined) return;
      const dist = Math.abs(px - xValue);
      if (!best || dist < best.dist) best = { pt, dataIndex, dist, parsed: { x: px, y: py } };
    });
    if (best) {
      nearestPoints.push({ datasetIndex, dataIndex: best.dataIndex, parsed: best.parsed, dataset: ds, raw: best.pt, distance: best.dist });
    }
  });
  return nearestPoints;
}
